import { apply, compose, invert } from './affine.ts';
import type { Affine, ImageTransform, Pt } from './types.ts';

/** Where a face usually is in a photo when nothing better is known (x, y as fractions). */
export const DEFAULT_FOCUS: Pt = [0.5, 0.42];

const dot = (p: Pt, q: Pt) => p[0] * q[0] + p[1] * q[1];

export function centroid(poly: Pt[]): Pt {
  let s = 0, cx = 0, cy = 0;
  poly.forEach((a, i) => {
    const b = poly[(i + 1) % poly.length];
    const f = a[0] * b[1] - b[0] * a[1];
    s += f; cx += (a[0] + b[0]) * f; cy += (a[1] + b[1]) * f;
  });
  return Math.abs(s) < 1e-12 ? poly[0] : [cx / (3 * s), cy / (3 * s)];
}

/**
 * The default fit of a picture in a zone: the map from image PIXELS to the
 * printed page (mm), such that the image covers the whole zone, stands upright
 * along `up`, and is slid until its focus point sits on the zone's centroid -
 * clamped so it still covers every corner.
 *
 * This is `placePhoto` from scripts/build-content-template.mjs, the fit the
 * approved photo previews use. The zones are triangles and wedges set at 45
 * degrees, so centring on the bounding box loses heads; the centroid and the
 * bounding-box centre of a triangle are nowhere near each other.
 *
 * @param outlines the zone's outline(s) on the page; the first is aimed at
 * @param up       the zone's up direction on the page (unit vector)
 */
export function coverPlacement(
  outlines: Pt[][], upIn: Pt, img: { width: number; height: number }, focus: Pt = DEFAULT_FOCUS,
): Affine {
  // documents round `up` to a few decimals, which is not quite unit length -
  // and a basis that is not orthonormal leaves the corners a pixel uncovered
  const n = Math.hypot(upIn[0], upIn[1]);
  const up: Pt = [upIn[0] / n, upIn[1] / n];
  const right: Pt = [-up[1], up[0]];
  const down: Pt = [-up[0], -up[1]];
  const pts = outlines.flat();
  const pr = pts.map((q) => dot(q, right));
  const pd = pts.map((q) => dot(q, down));
  const [r0, r1] = [Math.min(...pr), Math.max(...pr)];
  const [d0, d1] = [Math.min(...pd), Math.max(...pd)];
  const s = Math.max((r1 - r0) / img.width, (d1 - d0) / img.height);
  const dw = img.width * s, dh = img.height * s;

  const aim = centroid(outlines[0]);
  const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);
  const or0 = clamp(dot(aim, right) - focus[0] * dw, r1 - dw, r0);
  const od0 = clamp(dot(aim, down) - focus[1] * dh, d1 - dh, d0);
  const tl: Pt = [right[0] * or0 + down[0] * od0, right[1] * or0 + down[1] * od0];
  // pixel (u, v) -> tl + right * u * s + down * v * s
  return [right[0] * s, right[1] * s, down[0] * s, down[1] * s, tl[0], tl[1]];
}

/* ------------------------------------------------ the user's own adjustment */

/** No adjustment: the default fit exactly. */
export const DEFAULT_TRANSFORM: ImageTransform = {
  rotationDeg: 0, scale: 1, offsetX: 0, offsetY: 0, crop: { x: 0, y: 0, w: 1, h: 1 },
};

export const MIN_SCALE = 1;   // below the cover fit, bare paper would show
export const MAX_SCALE = 5;

/**
 * A zone's own upright frame on the page: `right` and `down` as the picture
 * will be seen on the folded card, the zone's size along each, and the point
 * zooming and turning happen about (its centroid).
 */
export function zoneFrame(outlines: Pt[][], upIn: Pt) {
  const n = Math.hypot(upIn[0], upIn[1]);
  const up: Pt = [upIn[0] / n, upIn[1] / n];
  const right: Pt = [-up[1], up[0]];
  const down: Pt = [-up[0], -up[1]];
  const pts = outlines.flat();
  const pr = pts.map((q) => dot(q, right));
  const pd = pts.map((q) => dot(q, down));
  return {
    right, down,
    width: Math.max(...pr) - Math.min(...pr),
    height: Math.max(...pd) - Math.min(...pd),
    pivot: centroid(outlines[0]),
  };
}

/**
 * Where a picture goes once the user has moved, zoomed or turned it: the
 * default fit, then the adjustment - which is stored in the zone's own
 * upright frame (brief s.8: parameters, never a changed image), so the same
 * numbers mean the same thing on screen, on the 45-degree printed sheet and
 * on the folded card.
 *
 *   offsetX / offsetY  fractions of the zone's upright width / height, along
 *                      the card's right / down
 *   scale              about the zone's centroid, 1 = the default fit
 *   rotationDeg        clockwise as seen on the card, about the centroid
 */
/**
 * A zone's outlines as its picture is laid out: a zone printed in pieces (the
 * surprise card) is placed on the whole it makes when folded - each outline
 * moved by its shift - and every other zone on its outlines as they are.
 */
export function frameOutlines(z: { outline: Pt[]; extraOutlines: Pt[][]; shifts?: Pt[] }): Pt[][] {
  const all = [z.outline, ...z.extraOutlines];
  return z.shifts ? all.map((o, k) => o.map((p) => [p[0] + z.shifts![k][0], p[1] + z.shifts![k][1]] as Pt)) : all;
}

/** Where part k of a zone printed in pieces draws its picture from: the whole, moved back to the piece. */
export function partShift(z: { shifts?: Pt[] }, k: number): Affine {
  const s = z.shifts?.[k] ?? [0, 0];
  return [1, 0, 0, 1, -s[0], -s[1]];
}

export function placeImage(
  outlines: Pt[][], up: Pt, img: { width: number; height: number },
  t: ImageTransform = DEFAULT_TRANSFORM, focus: Pt = DEFAULT_FOCUS,
): Affine {
  const base = coverPlacement(outlines, up, img, focus);
  if (t.scale === 1 && t.rotationDeg === 0 && t.offsetX === 0 && t.offsetY === 0) return base;
  const f = zoneFrame(outlines, up);
  const [cx, cy] = f.pivot;
  // right and down have the page's own handedness, so a clockwise turn on the
  // card is the ordinary y-down rotation on the page
  const a = (t.rotationDeg * Math.PI) / 180;
  const c = Math.cos(a) * t.scale, s = Math.sin(a) * t.scale;
  const tx = t.offsetX * f.width * f.right[0] + t.offsetY * f.height * f.down[0];
  const ty = t.offsetX * f.width * f.right[1] + t.offsetY * f.height * f.down[1];
  const about: Affine = [c, s, -s, c, cx - c * cx + s * cy + tx, cy - s * cx - c * cy + ty];
  return compose(about, base);
}

/** Does the picture, placed by `m`, cover every corner of the zone? */
export function covers(outlines: Pt[][], m: Affine, img: { width: number; height: number }, eps = 0.5): boolean {
  const back = invert(m);
  return outlines.flat().every((p) => {
    const [u, v] = apply(back, p);
    return u >= -eps && v >= -eps && u <= img.width + eps && v <= img.height + eps;
  });
}

const mix = (a: ImageTransform, b: ImageTransform, k: number): ImageTransform => ({
  ...b,
  rotationDeg: a.rotationDeg + (b.rotationDeg - a.rotationDeg) * k,
  scale: a.scale * (b.scale / a.scale) ** k,
  offsetX: a.offsetX + (b.offsetX - a.offsetX) * k,
  offsetY: a.offsetY + (b.offsetY - a.offsetY) * k,
});

/** How small a picture may be made in free placement (1 = the default fit). */
export const FREE_MIN_SCALE = 0.2;
/** How far a picture may be moved in free placement, in zone widths / heights. */
export const FREE_MAX_OFFSET = 1.5;

/**
 * Keep an adjustment sensible.
 *
 * `fill` (the first version's rule): move from `from` towards `to` only as far
 * as the picture still covers the whole zone - it can never expose bare paper,
 * but at the default fit it often cannot move at all, which people found too
 * limiting. `free`: move, zoom out and turn at will; only the extremes are
 * clamped, and the editor warns when blank paper would show (`covers`) and
 * offers `fillZone`.
 */
export function limitTransform(
  outlines: Pt[][], up: Pt, img: { width: number; height: number },
  from: ImageTransform, to: ImageTransform, mode: 'fill' | 'free' = 'fill',
): ImageTransform {
  if (mode === 'free') {
    const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);
    const turn = ((((to.rotationDeg + 180) % 360) + 360) % 360) - 180;
    return {
      ...to,
      rotationDeg: turn,
      scale: clamp(to.scale, FREE_MIN_SCALE, MAX_SCALE),
      offsetX: clamp(to.offsetX, -FREE_MAX_OFFSET, FREE_MAX_OFFSET),
      offsetY: clamp(to.offsetY, -FREE_MAX_OFFSET, FREE_MAX_OFFSET),
    };
  }
  const want = { ...to, scale: Math.min(MAX_SCALE, Math.max(MIN_SCALE, to.scale)) };
  if (covers(outlines, placeImage(outlines, up, img, want), img)) return want;
  if (!covers(outlines, placeImage(outlines, up, img, from), img)) return from;
  let lo = 0, hi = 1;
  for (let i = 0; i < 12; i++) {
    const k = (lo + hi) / 2;
    if (covers(outlines, placeImage(outlines, up, img, mix(from, want, k)), img)) lo = k;
    else hi = k;
  }
  return mix(from, want, lo);
}

/**
 * "Fill zone": keep the picture's angle and position, and zoom it just enough
 * to cover the whole zone again. If no zoom up to the maximum covers it from
 * where it is, bring it back to the centre and try again.
 */
export function fillZone(
  outlines: Pt[][], up: Pt, img: { width: number; height: number }, t: ImageTransform,
): ImageTransform {
  const ok = (s: number, x: number, y: number) =>
    covers(outlines, placeImage(outlines, up, img, { ...t, scale: s, offsetX: x, offsetY: y }), img);
  for (const [x, y] of [[t.offsetX, t.offsetY], [0, 0]]) {
    if (!ok(MAX_SCALE, x, y)) continue;
    let lo = Math.max(FREE_MIN_SCALE, Math.min(t.scale, MAX_SCALE)), hi = MAX_SCALE;
    if (ok(lo, x, y)) return { ...t, scale: lo, offsetX: x, offsetY: y };
    for (let i = 0; i < 20; i++) {
      const m = (lo + hi) / 2;
      if (ok(m, x, y)) hi = m; else lo = m;
    }
    return { ...t, scale: hi, offsetX: x, offsetY: y };
  }
  return DEFAULT_TRANSFORM;
}
